import React, { useEffect, useState } from "react";
import { Pages } from "@/lib/api";
import { Phone, X } from "lucide-react";

const stripPara = (html) => (html || "").replace(/<[^>]+>/g, "").trim();
const DISMISS_KEY = "atc-emergency-banner-dismissed";

/**
 * Mobile-first "We're available 24/7 — tap to call" banner.
 * Slides up from the bottom on mobile shortly after page load.
 * Reads the live phone number from the home page CMS, falls back gracefully.
 * Dismissed state persists across the session via sessionStorage.
 */
const EmergencyBanner = () => {
  const [phone, setPhone] = useState("");
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    (async () => {
      try {
        const home = await Pages.get("home");
        const raw = stripPara(home.sections?.contact_phone) || "";
        setPhone(raw);
      } catch {}
      setTimeout(() => setShown(true), 1200);
    })();
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setShown(false);
  };

  if (!shown) return null;

  const telHref = `tel:${phone.replace(/[^\d+]/g, "")}`;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-40 md:hidden animate-fade-up"
      data-testid="emergency-banner"
    >
      <div className="glass rounded-2xl border border-atc-green/40 shadow-[0_20px_60px_-10px_rgba(63,191,63,0.5)] overflow-hidden">
        <div className="flex items-stretch">
          <div className="flex-1 p-4 pr-2">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-atc-green font-semibold">
              <span className="relative flex h-2 w-2">
                <span className="absolute inset-0 rounded-full bg-atc-green animate-ping opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-atc-green" />
              </span>
              Interested in learning more?
            </div>
            <div className="text-white text-sm font-semibold leading-tight mt-1.5">
              The clock&apos;s ticking — tap to call now.
            </div>
            {phone && (
              <div className="text-xs text-slate-300 mt-0.5">{phone}</div>
            )}
          </div>
          <a
            href={telHref}
            className="flex items-center justify-center px-5 brand-gradient text-white text-sm font-semibold shrink-0"
            data-testid="emergency-call-button"
          >
            <Phone className="h-5 w-5 mr-2" /> Call
          </a>
          <button
            onClick={dismiss}
            className="absolute top-1 right-1 p-1.5 text-slate-300 hover:text-white"
            aria-label="Dismiss"
            data-testid="emergency-dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmergencyBanner;
